import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ReactSVG } from 'react-svg';
import {
  JoinRetailerMutationVariables,
  useJoinRetailerMutation,
} from './joinRetailerMutation.generated';
import { Button, ModalWrapper, PortalModal } from '@web/utils/src';
import plusIcon from '@web/utils/src/assets/icons/functions/plus-icon.svg';
import { Logo } from '@/components/Logo';
import { useCustomToast } from '@/hooks/useCustomToast';
import { Retailer } from '@/types';

export interface JoinRetailerModalProps {
  isOpen: boolean;
  setIsOpen: (arg1: boolean) => void;
  retailer?: Retailer;
}

enum ModalState {
  ENTER_CODE,
  SEND_REQUEST,
}

const JoinRetailerModal = (props: JoinRetailerModalProps) => {
  const { isOpen, setIsOpen, retailer } = props;
  const [state, setState] = useState(ModalState.ENTER_CODE);
  const [code, setCode] = useState('');
  const [message, setMessage] = useState('');
  const showToast = useCustomToast();

  const { t } = useTranslation();
  const navigate = useNavigate();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, joinRetailerMutation] = useJoinRetailerMutation();
  const performJoinRetailerMutation = async (
    variables: JoinRetailerMutationVariables,
  ) => {
    const result = await joinRetailerMutation(variables);
    if (!result.error) {
      setIsOpen(false);
      return true;
    }
    return false;
  };

  const handleJoinRetailer = async () => {
    if (!code) return;

    const variables: JoinRetailerMutationVariables = {
      retailerId: retailer?.id as string,
      inquiryText: null,
      customerNumber: code,
    };

    if (!(await performJoinRetailerMutation(variables))) {
      showToast('error', t('farmers_homepage.join_retailer.invalid_code'));
    } else {
      navigate('/' + retailer?.id);
    }
  };

  const handleRequestRetailerAcces = async () => {
    if (!message) return;

    const variables: JoinRetailerMutationVariables = {
      retailerId: retailer?.id as string,
      inquiryText: message,
      customerNumber: null,
    };

    if (!(await performJoinRetailerMutation(variables))) {
      showToast('error', t('farmers_homepage.join_retailer.request_failed'));
    } else {
      showToast('success', t('farmers_homepage.join_retailer.request_sent'));
    }
  };

  return (
    <PortalModal isOpen={isOpen} setIsOpen={setIsOpen}>
      <ModalWrapper
        setIsOpen={setIsOpen}
        submitHandler={() => {
          return;
        }}
        controls={false}
        t={t}
        header={false}
      >
        <>
          <div className="header relative mb-2-0">
            <div className="h-[30px]">
              <Logo disabledLink className="h-full max-h-100-percent" />
            </div>

            <div className="rotate-45 absolute top-0 right-1-0 cursor-pointer opacity-50">
              <ReactSVG
                data-testid="close"
                src={plusIcon}
                onClick={() => setIsOpen(false)}
              />
            </div>
          </div>
          {state === ModalState.ENTER_CODE && (
            <>
              <div className="text-left">
                <p className="font-bd text-1-8 md:text-2-0 text-primary-black mb-0-5">
                  {t(
                    'farmers_homepage.join_retailer.are_you_existing_customer',
                  )}
                </p>
                <p className="font-rg text-1-4 md:text-1-6 text-primary-black mb-1-0">
                  {t('farmers_homepage.join_retailer.if_yes_code')}
                </p>
                <div className="py-2-0 md:py-1-0">
                  <p className="mb-1-0 font-li">
                    {t('farmers_homepage.join_retailer.account_number')}
                  </p>
                  <input
                    type="text"
                    className="rounded-base border border-primary-grey w-full text-body1 p-1-0 mb-1-0"
                    onChange={(e) => setCode(e.target.value)}
                    value={code}
                  />
                  <p className="text-2-0 md:text-1-4 font-li">
                    *{t('farmers_homepage.join_retailer.details_term')}
                  </p>
                </div>
              </div>
              <div className="flex justify-between gap-2-0 mt-1-0">
                <Button
                  variant="secondary"
                  className="w-full"
                  onClick={() => setState(ModalState.SEND_REQUEST)}
                >
                  {t('farmers_homepage.join_retailer.no_account_number')}
                </Button>
                <Button className="w-full" onClick={handleJoinRetailer}>
                  {t('farmers_homepage.join_retailer.enter_store')}
                </Button>
              </div>
            </>
          )}
          {state === ModalState.SEND_REQUEST && (
            <div className="text-left">
              <p className="font-bd text-1-8 md:text-2-0 text-primary-black mb-0-5">
                {t('farmers_homepage.join_retailer.request_access')}
              </p>
              <p className="font-rg text-1-4 md:text-1-6 text-primary-black mb-2-0">
                {t('farmers_homepage.join_retailer.subitle')}
              </p>

              <div className="py-2-0 md:py-1-0">
                <p className="mb-1-0 font-li">
                  {t('farmers_homepage.join_retailer.message_here')}
                </p>
                <textarea
                  className="rounded-base border border-primary-grey w-full text-body1 p-1-0 mb-1-0 resize-none"
                  rows={5}
                  onChange={(e) => setMessage(e.target.value)}
                  value={message}
                />
                <p className="text-2-0 md:text-1-4 font-li">
                  *{t('farmers_homepage.join_retailer.details_term')}
                </p>
              </div>
              <div className="flex justify-between gap-2-0 mt-1-0">
                <Button
                  variant="secondary"
                  className="w-full"
                  onClick={() => setState(ModalState.ENTER_CODE)}
                >
                  {t('farmers_homepage.join_retailer.back')}
                </Button>
                <Button className="w-full" onClick={handleRequestRetailerAcces}>
                  {t('farmers_homepage.join_retailer.send_request')}
                </Button>
              </div>
            </div>
          )}
        </>
      </ModalWrapper>
    </PortalModal>
  );
};

export default JoinRetailerModal;
